import { Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, useMediaQuery } from "@mui/material";
import { type Breakpoint, type SxProps, type Theme, useTheme } from "@mui/material/styles";
import type { ReactNode } from "react";

import { useLabel } from "@mr-portfolio-infrastructure/translations/use-label";

import { AsyncButton, AsyncButtonStartIconValues } from "../async-button";

export interface BaseDialogProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  contentText?: ReactNode;
  children?: ReactNode;
  additionalActions?: ReactNode;
  cancelButtonTitle?: ReactNode;
  hideCancelButton?: boolean;
  maxWidth?: Breakpoint | false;
  fullScreen?: boolean;
  stickToTop?: boolean;
  disableBackdropClickClose?: boolean;
  sx?: SxProps<Theme>;
}

export function BaseDialog(props: BaseDialogProps): React.JSX.Element {
  const {
    open,
    onClose,
    title,
    contentText,
    children,
    additionalActions,
    cancelButtonTitle,
    hideCancelButton = false,
    maxWidth = "sm",
    fullScreen = false,
    stickToTop = false,
    disableBackdropClickClose = false,
    sx,
  } = props;

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const getLabel = useLabel();
  const isFullScreen = fullScreen || isSmallScreen;

  return (
    <Dialog
      open={open}
      fullWidth
      fullScreen={isFullScreen}
      maxWidth={maxWidth}
      scroll="body"
      aria-labelledby="base-dialog-title"
      sx={sx ?? []}
      onClose={(_event, reason) => {
        if (reason !== "backdropClick" || !disableBackdropClickClose) {
          onClose();
        }
      }}
      slotProps={{ paper: stickToTop ? { sx: { verticalAlign: "top" } } : {} }}
    >
      {(title !== undefined || isFullScreen) && (
        <DialogTitle id="base-dialog-title">
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ flexGrow: 1 }}>{title}</Box>
            {isFullScreen && (
              <AsyncButton
                iconButton
                startIconValue={AsyncButtonStartIconValues.CloseIconValue}
                tooltip={getLabel("app.buttons.close")}
                onClick={onClose}
              />
            )}
          </Box>
        </DialogTitle>
      )}
      <DialogContent>
        {contentText !== undefined && <DialogContentText sx={{ mb: 2 }}>{contentText}</DialogContentText>}
        {children}
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={2}>
          {additionalActions}
          {!hideCancelButton && (
            <AsyncButton color="secondary" onClick={onClose}>
              {cancelButtonTitle ?? getLabel("app.buttons.close")}
            </AsyncButton>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
